/**
 * Main Game Class
 * Handles game loop, state management, and coordination of all systems
 */

import { THREE } from "../config/ThreeConfig.js";
import { Scene } from './Scene.js';
import { createPlayer } from '../player/Characters.js';
import { CyberUIManager } from '../ui/CyberUIManager.js';
import { SoundManager } from '../audio/SoundManager.js';
import { initGameplayFeedback } from '../utils/GameplayFeedback.js';
import { LevelManager } from '../levels/LevelManager.js';
import { EnemyManager } from '../enemies/EnemyManager.js';
import { PowerUpManager } from '../utils/PowerUpManager.js';
import { DialogueManager } from '../utils/DialogueManager.js';
import { AutoplayPilot } from "../utils/AutoplayPilot.js";
import { FriendlyManager } from "../utils/FriendlyManager.js";
import { createPostFX } from "./PostFX.js";
import { getAimDirectionThroughReticle } from "../utils/reticleAim.js";
import { isTestEnvironment } from "../utils/runtimeEnv.js";

function normalizeCharacterId(characterId) {
  return String(characterId || "honeybadger")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

export class Game {
  constructor(options) {
    this.canvas = options.canvas;
    this.uiLayer = options.uiLayer;
    this.portalData = options.portalData || {};
    this.onProgress = options.onProgress || (() => {});
    this.rendererFactory =
      options.rendererFactory ||
      ((rendererOptions) => new THREE.WebGLRenderer(rendererOptions));
    
    // Core systems
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.clock = new THREE.Clock();
    this._animationId = null;
    
    // Game state
    this.state = {
      isRunning: false,
      isPaused: false,
      score: 0,
      equity: 0,
      lives: 3,
      currentLevel: 0,
      difficulty: 'normal',
    };
    this.missionStats = this.createMissionStats();
    
    // Game objects
    this.player = null;
    this.enemies = [];
    this.powerUps = [];
    this.projectiles = [];
    this.friendlies = [];
    
    // Managers
    this.sceneManager = null;
    this.uiManager = null;
    this.soundManager = null;
    this.levelManager = null;
    this.enemyManager = null;
    this.powerUpManager = null;
    this.friendlyManager = null;
    this.feedback = null;
    
    // Input state
    this.input = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      stickX: 0,
      stickY: 0,
      fire: false,
      boost: false,
      brake: false,
      bomb: false,
      roll: false,
      special: false,
      device: "keyboard",
    };

    // Barrel roll edge-detection + double-tap timing (Star Fox style).
    this._rollKeyArmed = false;
    this._lastLeftTapAt = 0;
    this._lastRightTapAt = 0;
    this._gamepadPrevRollPressed = false;
    this._gamepadPrevSpecialPressed = false;
    this._specialQueued = false;
    this._specialHudAcc = 0;
    this._mouseX = Math.max(1, (globalThis.window?.innerWidth || 1) * 0.5);
    this._mouseY = Math.max(1, (globalThis.window?.innerHeight || 1) * 0.45);
    this._mouseInitialized = false;
    this._mouseDown = false;
    this._onPointerMove = null;
    this._onMouseMove = null;
    this._onMouseDown = null;
    this._onMouseUp = null;
    this._onPointerLockChange = null;
    this._pointerLocked = false;
    this._pointerAimSensitivity = 1.0;
    
    // Bind methods
    this.animate = this.animate.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this._onGamepadConnected = null;
    this._onGamepadDisconnected = null;

    // Collision cooldowns (prevents per-frame damage + HUD spam when bounding boxes overlap)
    this._collisionDamageAt = new WeakMap();
    this._friendlyCollisionAt = new WeakMap();

    this._didSetupInput = false;
    this._didLoadInitialLevel = false;

    // Demo/autoplay support (for feel testing).
    this.autoplayEnabled = false;
    this.autoplayPilot = null;

    // Optional post-processing (subtle bloom for bright FX only).
    this.postFX = null;
  }
  
  async init() {
    this.onProgress(30, 'Setting up renderer...');
    
    // Initialize renderer
    this.renderer = this.rendererFactory({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    if (!this.renderer) throw new Error("Renderer factory returned no renderer");
    
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    // Vibe Jam build: prioritize stable FPS over ultra-crisp retina rendering.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.25));

    // Space combat reads better with emissive + rim lighting than with expensive shadow passes.
    this.renderer.shadowMap.enabled = false;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ReinhardToneMapping;
    // Slightly more vibrant default (space assets + emissive read better with a bit more exposure).
    this.renderer.toneMappingExposure = 1.25;
    
    this.onProgress(40, 'Creating scene...');
    
    // Initialize scene manager
    this.sceneManager = new Scene();
    await this.sceneManager.init();
    
    this.scene = this.sceneManager.scene;
    this.camera = this.sceneManager.camera;

    await this.initPostFX();
    
    this.onProgress(50, 'Loading managers...');
    
    // Initialize managers
    this.uiManager = new CyberUIManager(this.uiLayer, this.state);
    await this.uiManager.init();
    
    this.soundManager = new SoundManager();
    await this.soundManager.init();

    // Initialize feedback system
    this.feedback = initGameplayFeedback(this.uiManager, this.sceneManager);

    this.levelManager = new LevelManager(this);
    await this.levelManager.init();
    
    this.enemyManager = new EnemyManager(this);
    await this.enemyManager.init();
    
    this.powerUpManager = new PowerUpManager(this);
    await this.powerUpManager.init();

    this.friendlyManager = new FriendlyManager(this);

    this.dialogueManager = new DialogueManager(this);
    
    // Aliases used across the codebase
    this.enemies = this.enemyManager.enemies;

    this.powerUps = this.powerUpManager.powerUps;
    this.friendlies = this.friendlyManager.friendlies;
    
    this.onProgress(70, 'Creating player...');
    
    // Create an initial player, but defer the pre-launch overlays + first level load
    // until `start()` so we don't schedule spawns behind selection screens.
    await this.setPlayerCharacter(normalizeCharacterId(this.portalData.character));
    
    this.onProgress(95, 'Ready to launch...');
  }

  shouldEnableBloom() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("bloom") === "0") return false;
      if (params.get("bloom") === "1") return true;
      if (isTestEnvironment()) return false;
      const reducedData =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-data: reduce)")?.matches;
      if (reducedData) return false;
      return (window.innerWidth || 0) >= 900 && (window.innerHeight || 0) >= 540;
    } catch {
      return false;
    }
  }

  async initPostFX() {
    if (!this.renderer || !this.scene || !this.camera) return;
    if (!this.shouldEnableBloom()) return;
    try {
      this.postFX = await createPostFX(this.renderer, this.scene, this.camera, {
        width: window.innerWidth,
        height: window.innerHeight,
        pixelRatio: Math.min(window.devicePixelRatio || 1, 1.25),
        bloomStrength: 0.16,
        bloomRadius: 0.16,
        bloomThreshold: 0.9,
      });
    } catch {
      this.postFX = null;
    }
  }

  enableAutoplay(options = {}) {
    this.autoplayEnabled = true;
    this.autoplayPilot = new AutoplayPilot(options);
  }

  async setPlayerCharacter(characterId, options = {}) {
    const { preservePosition = false } = options;
    const id = normalizeCharacterId(characterId);

    if (this.player && normalizeCharacterId(this.player.character) === id) return this.player;

    const prev = this.player;
    const prevPos = prev?.object3D?.position?.clone?.();

    if (prev?.object3D) this.scene?.remove?.(prev.object3D);
    if (prev && typeof prev.dispose === "function") {
      try {
        prev.dispose();
      } catch {
        // ignore
      }
    }

    this.player = createPlayer(this, id, {
      color: this.portalData.color,
      username: this.portalData.username,
    });

    await this.player.init();
    if (preservePosition && prevPos) this.player.object3D.position.copy(prevPos);
    this.scene.add(this.player.object3D);

    this.uiManager?.updateCharacter?.(this.player.character);
    return this.player;
  }

  setupFeedbackHooks() {
    if (!this.player || !this.feedback) return;

    // Hook into player events
    this.player.onTakeDamage = (amount = 0) => {
      this.recordDamageTaken(amount);
      this.feedback.playHitEffect();
      this.feedback.shakeScreen(0.2, 0.3);
      // Portrait reaction (best effort)
      const hp = Number(this.player?.health ?? 100);
      const maxHp = Number(this.player?.maxHealth ?? 100);
      const ratio = maxHp > 0 ? hp / maxHp : 1;
      const expr = ratio <= 0.35 ? "damaged" : "shocked";
      this.uiManager?.setCharacterExpression?.(this.player?.character, expr, { ttlMs: expr === "damaged" ? 1400 : 850 });
    };

    this.player.onBoost = () => {
      this.feedback.playBoostEffect();
      this.uiManager?.setCharacterExpression?.(this.player?.character, "angry", { ttlMs: 650 });
    };

    this.player.onBomb = () => {
      this.recordBombUsed();
      this.feedback.playBombEffect();
      this.uiManager?.setCharacterExpression?.(this.player?.character, "smug", { ttlMs: 1000 });
    };
  }

  setupInput() {
    if (this._didSetupInput) return;
    this._didSetupInput = true;

    // Keyboard input
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    
    // Gamepad support (checked in animate loop)
    this.gamepadIndex = null;
    
    this._onGamepadConnected = (e) => {
      this.gamepadIndex = e.gamepad.index;
      console.log('Gamepad connected:', e.gamepad.id);
    };
    
    this._onGamepadDisconnected = (e) => {
      if (this.gamepadIndex === e.gamepad.index) {
        this.gamepadIndex = null;
      }
    };

    window.addEventListener('gamepadconnected', this._onGamepadConnected);
    window.addEventListener('gamepaddisconnected', this._onGamepadDisconnected);

    // Mouse / trackpad aiming. Store intent here; the HUD reticle is rendered every frame
    // from the same aim model used by projectiles so it never waits on another mouse event.
    if (this.canvas) this.canvas.style.cursor = 'none';
    const setPointerAim = (clientX, clientY) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      const x = Number(clientX);
      const y = Number(clientY);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      this._mouseX = THREE.MathUtils.clamp(x, w * 0.02, w * 0.98);
      this._mouseY = THREE.MathUtils.clamp(y, h * 0.02, h * 0.98);
      this._mouseInitialized = true;
      this.input.device = "mouse";
    };
    const applyPointerDelta = (movementX, movementY) => {
      const dx = Number(movementX);
      const dy = Number(movementY);
      if (!Number.isFinite(dx) || !Number.isFinite(dy) || (dx === 0 && dy === 0)) return false;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      const baseX = Number.isFinite(Number(this._mouseX)) ? this._mouseX : w * 0.5;
      const baseY = Number.isFinite(Number(this._mouseY)) ? this._mouseY : h * 0.45;
      const sensitivity = Number(this._pointerAimSensitivity) || 1.25;
      setPointerAim(baseX + dx * sensitivity, baseY + dy * sensitivity);
      return true;
    };
    const isPointerLocked = () => {
      try {
        return document.pointerLockElement === this.canvas;
      } catch {
        return false;
      }
    };
    const handlePointerIntent = (e) => {
      if (isPointerLocked() && applyPointerDelta(e.movementX, e.movementY)) return;
      const x = Number(e.clientX);
      const y = Number(e.clientY);
      if ((!Number.isFinite(x) || !Number.isFinite(y)) && applyPointerDelta(e.movementX, e.movementY)) return;
      setPointerAim(x, y);
    };
    const pointerLockDisabled = () => {
      try {
        return new URLSearchParams(window.location.search).get("nopointerlock") === "1";
      } catch {
        return false;
      }
    };
    const maybeRequestPointerLock = () => {
      if (!this.canvas?.requestPointerLock || pointerLockDisabled() || isPointerLocked()) return;
      try {
        const result = this.canvas.requestPointerLock({ unadjustedMovement: true });
        result?.catch?.(() => {
          try {
            this.canvas.requestPointerLock();
          } catch {
            // Pointer lock is a feel upgrade, not a startup requirement.
          }
        });
      } catch {
        try {
          this.canvas.requestPointerLock();
        } catch {
          // ignore
        }
      }
    };

    setPointerAim(this._mouseX, this._mouseY);

    this._onPointerMove = (e) => {
      handlePointerIntent(e);
    };
    this._onMouseMove = (e) => {
      handlePointerIntent(e);
    };
    this._onMouseDown = (e) => {
      if (e.button === 0) {
        handlePointerIntent(e);
        maybeRequestPointerLock();
        this._mouseDown = true;
        this.input.fire = true;
      }
    };
    this._onMouseUp = (e) => {
      if (e.button === 0) {
        this._mouseDown = false;
        this.input.fire = false;
      }
    };
    this._onPointerLockChange = () => {
      this._pointerLocked = isPointerLocked();
    };
    if (typeof window.PointerEvent !== "undefined") {
      window.addEventListener('pointermove', this._onPointerMove, { passive: true });
      if ('onpointerrawupdate' in window) {
        window.addEventListener('pointerrawupdate', this._onPointerMove, { passive: true });
      }
    }
    window.addEventListener('mousemove', this._onMouseMove, { passive: true });
    window.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mouseup', this._onMouseUp);
    document.addEventListener('pointerlockchange', this._onPointerLockChange);
  }
  
  onKeyDown(event) {
    if (event?.repeat) return;
    this.input.device = "keyboard";
    const nowMs =
      (globalThis.performance && typeof globalThis.performance.now === "function")
        ? globalThis.performance.now()
        : Date.now();

    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.input.forward = true;
        this._syncDigitalStickAxes();
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.input.backward = true;
        this._syncDigitalStickAxes();
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.input.left = true;
        this._syncDigitalStickAxes();
        if (nowMs - this._lastLeftTapAt <= 240) this.input.roll = true;
        this._lastLeftTapAt = nowMs;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.input.right = true;
        this._syncDigitalStickAxes();
        if (nowMs - this._lastRightTapAt <= 240) this.input.roll = true;
        this._lastRightTapAt = nowMs;
        break;
      case 'Space':
        this.input.fire = true;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.input.boost = true;
        break;
      case 'ControlLeft':
      case 'ControlRight':
        this.input.brake = true;
        break;
      case 'KeyX':
        this.input.bomb = true;
        break;
      case 'KeyC':
        this.input.roll = true;
        break;
      case 'KeyE':
        this._specialQueued = true;
        this.input.special = true;
        break;
      case 'KeyP':
      case 'Escape':
        this.togglePause();
        break;
      case 'KeyH':
        this.uiManager?.toggleHUD?.();
        break;
    }
  }
  
  onKeyUp(event) {
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.input.forward = false;
        this._syncDigitalStickAxes();
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.input.backward = false;
        this._syncDigitalStickAxes();
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.input.left = false;
        this._syncDigitalStickAxes();
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.input.right = false;
        this._syncDigitalStickAxes();
        break;
      case 'Space':
        this.input.fire = false;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.input.boost = false;
        break;
      case 'ControlLeft':
      case 'ControlRight':
        this.input.brake = false;
        break;
      case 'KeyX':
        this.input.bomb = false;
        break;
      case 'KeyE':
        // Do not clear `_specialQueued`; a quick tap can begin and end between
        // animation frames, and the next frame still needs to see the edge.
        if (!this._specialQueued) this.input.special = false;
        break;
    }
  }

  _syncDigitalStickAxes() {
    const digitalX = (this.input.right ? 1 : 0) + (this.input.left ? -1 : 0);
    const digitalY = (this.input.forward ? 1 : 0) + (this.input.backward ? -1 : 0);
    this.input.stickX = THREE.MathUtils.clamp(digitalX, -1, 1);
    this.input.stickY = THREE.MathUtils.clamp(digitalY, -1, 1);
  }
  
  updateInput() {
    // Gamepad input
    if (this.gamepadIndex !== null) {
      const gamepad = navigator.getGamepads()[this.gamepadIndex];
      if (gamepad) {
        this.input.device = "gamepad";
        const axisXRaw = Number(gamepad.axes?.[0]) || 0;
        const axisYRaw = Number(gamepad.axes?.[1]) || 0;
        const axisDeadzone = 0.08;
        const axisX = Math.abs(axisXRaw) >= axisDeadzone ? axisXRaw : 0;
        const axisY = Math.abs(axisYRaw) >= axisDeadzone ? -axisYRaw : 0;

        const dpadUp = gamepad.buttons[12]?.pressed || false;
        const dpadDown = gamepad.buttons[13]?.pressed || false;
        const dpadLeft = gamepad.buttons[14]?.pressed || false;
        const dpadRight = gamepad.buttons[15]?.pressed || false;

        const digitalX = (dpadRight ? 1 : 0) + (dpadLeft ? -1 : 0);
        const digitalY = (dpadUp ? 1 : 0) + (dpadDown ? -1 : 0);

        this.input.stickX = THREE.MathUtils.clamp(axisX + digitalX, -1, 1);
        this.input.stickY = THREE.MathUtils.clamp(axisY + digitalY, -1, 1);

        const buttonThreshold = 0.3;
        this.input.forward = this.input.stickY > buttonThreshold;
        this.input.backward = this.input.stickY < -buttonThreshold;
        this.input.left = this.input.stickX < -buttonThreshold;
        this.input.right = this.input.stickX > buttonThreshold;
        
        // Buttons
        this.input.fire = gamepad.buttons[0]?.pressed || false; // A button
        this.input.boost = gamepad.buttons[7]?.pressed || false; // RT
        this.input.brake = gamepad.buttons[6]?.pressed || false; // LT
        this.input.bomb = gamepad.buttons[1]?.pressed || false; // B button

        const rollPressed = gamepad.buttons[5]?.pressed || false; // RB
        this.input.roll = rollPressed && !this._gamepadPrevRollPressed;
        this._gamepadPrevRollPressed = rollPressed;

        const specialPressed = gamepad.buttons[3]?.pressed || false; // Y / top face button
        this.input.special = specialPressed && !this._gamepadPrevSpecialPressed;
        this._gamepadPrevSpecialPressed = specialPressed;

        if (gamepad.buttons[9]?.pressed) { // Start button
          this.togglePause();
        }
        return;
      }
    }

    // Keyboard-only or disconnected gamepad path.
    this.input.device = "keyboard";
    this._syncDigitalStickAxes();
    if (this._specialQueued) {
      this.input.special = true;
      this._specialQueued = false;
    }
  }
  
  async start() {
    if (this.state.isRunning) return;

    // Setup input listeners only when we’re about to actually run.
    this.setupInput();

    // Load the initial level only when starting (avoids scheduling spawns during pre-launch overlays).
    if (!this._didLoadInitialLevel) {
      this._didLoadInitialLevel = true;
      await this.levelManager.loadLevel(0);
    }

    this.state.isRunning = true;
    this.state.isPaused = false;
    this.clock.start();
    this.animate();
    this.soundManager.playMusic('main');
  }
  
  pause() {
    this.state.isPaused = true;
    this.uiManager.showPauseMenu();
    this.clock.stop();
    this.soundManager.pauseMusic();
  }
  
  resume() {
    this.state.isPaused = false;
    this.uiManager.hidePauseMenu();
    this.soundManager.resumeMusic();
    this.clock.start();
  }
  
  togglePause() {
    if (this.state.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }
  
  gameOver() {
    this.state.isRunning = false;
    this.uiManager.showGameOver(this.state.score);
    this.soundManager.playSFX('gameover');
  }
  
  victory() {
    this.state.isRunning = false;
    this.uiManager.showVictory(this.state.score, this.state.equity);
    this.soundManager.playMusic('victory');
  }
  
  addScore(points) {
    this.state.score += points;
    this.uiManager.updateScore(this.state.score);
  }
  
  addEquity(amount) {
    this.state.equity += amount;
    this.uiManager.updateEquity(this.state.equity);
  }

  createMissionStats(levelIndex = this.state?.currentLevel || 0) {
    const now =
      (globalThis.performance && typeof globalThis.performance.now === "function")
        ? globalThis.performance.now()
        : Date.now();
    return {
      levelIndex,
      startedAtMs: now,
      kills: 0,
      bosses: 0,
      damageTaken: 0,
      bombsUsed: 0,
      scoreAtStart: Number(this.state?.score) || 0,
    };
  }

  resetMissionStats(levelIndex = this.state?.currentLevel || 0) {
    this.missionStats = this.createMissionStats(levelIndex);
    return this.missionStats;
  }

  recordEnemyDestroyed(enemy) {
    if (!this.missionStats) this.resetMissionStats();
    this.missionStats.kills += 1;
    if (enemy?.isBoss) this.missionStats.bosses += 1;
  }

  recordDamageTaken(amount = 0) {
    if (!this.missionStats) this.resetMissionStats();
    this.missionStats.damageTaken += Math.max(0, Number(amount) || 0);
  }

  recordBombUsed() {
    if (!this.missionStats) this.resetMissionStats();
    this.missionStats.bombsUsed += 1;
  }

  getReticleSoftLockDirection(origin, camera = this.sceneManager?.camera || this.camera) {
    if (!origin?.isVector3 || !camera || !Array.isArray(this.enemies)) return null;
    const width = this.renderer?.domElement?.clientWidth || window.innerWidth || 1;
    const height = this.renderer?.domElement?.clientHeight || window.innerHeight || 1;
    const reticleX = Number.isFinite(Number(this._mouseX)) ? Number(this._mouseX) : width * 0.5;
    const reticleY = Number.isFinite(Number(this._mouseY)) ? Number(this._mouseY) : height * 0.45;
    const radiusPx = 58;

    let best = null;
    let bestDist = Infinity;
    for (const enemy of this.enemies) {
      if (!enemy?.object3D?.position || enemy.isDestroyed) continue;
      const projected = enemy.object3D.position.clone().project(camera);
      if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y) || !Number.isFinite(projected.z)) continue;
      if (projected.z > 1) continue;

      const sx = (projected.x * 0.5 + 0.5) * width;
      const sy = (-projected.y * 0.5 + 0.5) * height;
      const dist = Math.hypot(sx - reticleX, sy - reticleY);
      if (dist <= radiusPx && dist < bestDist) {
        best = enemy;
        bestDist = dist;
      }
    }

    if (!best?.object3D?.position) return null;
    const targetPoint = best.object3D.position.clone();
    const direction = targetPoint.sub(origin);
    if (!Number.isFinite(direction.x) || !Number.isFinite(direction.y) || !Number.isFinite(direction.z)) return null;
    if (direction.lengthSq() < 1e-6) return null;
    return direction.normalize();
  }

  // Aim direction derived from the on-screen reticle (so shots match the crosshair).
  getAimDirection(origin) {
    const pos = (origin && origin.isVector3) ? origin : (this.player?.object3D?.position?.clone?.() || new THREE.Vector3());
    const camera = this.sceneManager?.camera || this.camera;

    const softLockDirection =
      typeof this.getReticleSoftLockDirection === "function"
        ? this.getReticleSoftLockDirection(pos, camera)
        : Game.prototype.getReticleSoftLockDirection.call(this, pos, camera);
    if (softLockDirection) return softLockDirection;

    const reticleDirection = getAimDirectionThroughReticle(pos, this.player, camera, this.renderer, this._mouseX || 0, this._mouseY || 0);
    if (reticleDirection) return reticleDirection;

    if (this.player?.object3D) {
      const q = this.player.object3D.quaternion;
      if (q?.isQuaternion) {
        const fallbackForward = new THREE.Vector3(0, 0, -1).applyQuaternion(q).normalize();
        if (fallbackForward.lengthSq() > 1e-6) return fallbackForward;
      }
    }

    if (camera?.getWorldDirection) {
      const fallback = new THREE.Vector3();
      camera.getWorldDirection(fallback);
      if (fallback.lengthSq() > 1e-6) return fallback.normalize();
    }
    return new THREE.Vector3(0, 0, -1);
  }
  
  loseLife() {
    this.state.lives--;
    this.uiManager.updateLives(this.state.lives);
    
    if (this.state.lives <= 0) {
      this.gameOver();
    } else {
      this.player.respawn();
    }
  }
  
  animate() {
    this._animationId = requestAnimationFrame(this.animate);
    
    if (!this.state.isRunning || this.state.isPaused) return;
    
    const deltaTime = this.clock.getDelta();
    
    // Update input
    this.updateInput();

    // Autoplay overrides input after polling keyboard/gamepad.
    if (this.autoplayEnabled && this.autoplayPilot) {
      try {
        const nowMs =
          (globalThis.performance && typeof globalThis.performance.now === "function")
            ? globalThis.performance.now()
            : Date.now();
        this.autoplayPilot.start(nowMs);
        this.input = this.autoplayPilot.getInput(nowMs, this);
      } catch {
        // ignore
      }
    }
    
    // Update player
    if (this.player) {
      this.player.update(deltaTime, this.input);
    }

    // Special ability HUD indicator (throttled so we don't hammer DOM every frame).
    this._specialHudAcc += deltaTime;
    if (this.player && this.uiManager && this._specialHudAcc >= 0.1) {
      this._specialHudAcc = 0;
      try {
        const nowMs =
          (globalThis.performance && typeof globalThis.performance.now === "function")
            ? globalThis.performance.now()
            : Date.now();
        const st = this.player.getSpecialHudState?.(nowMs);
        if (st) this.uiManager.updateSpecialAbility?.(st);
      } catch {
        // ignore
      }
    }
    
    // Update camera (follow player)
    if (this.player && this.sceneManager) {
      this.sceneManager.updateCamera(this.player, deltaTime);
    }

    // Reticle follows the ship (screen-space aim point).
    if (this.player && this.uiManager?.updateReticle) {
      try {
        this.uiManager.updateReticle(this.player, this.camera, this.renderer, deltaTime);
      } catch {
        // ignore
      }
    }

    // Charge/lock-on targeting update (depends on camera + reticle placement).
    if (this.player && typeof this.player.updateChargeLock === "function") {
      try {
        this.player.updateChargeLock(deltaTime);
      } catch {
        // ignore
      }
    }
    
    // Update enemies
    if (this.enemyManager) {
      this.enemyManager.update(deltaTime, this.player);
    }

    // Update friendlies (civilian freighters, etc.)
    if (this.friendlyManager) {
      this.friendlyManager.update(deltaTime);
    }
    
    // Update power-ups
    if (this.powerUpManager) {
      this.powerUpManager.update(deltaTime, this.player);
    }
    
    // Update projectiles
    this.updateProjectiles(deltaTime);
    
    // Check collisions
    this.checkCollisions();
    
    // Update UI
    this.uiManager.update(deltaTime);
    if (this.feedback && this.feedback.update) this.feedback.update(deltaTime);
    
    // Render
    if (this.postFX?.composer) this.postFX.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }
  
  updateProjectiles(deltaTime) {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      projectile.update(deltaTime);
      
      // Remove if out of bounds or expired
      if (projectile.isExpired()) {
        this.scene.remove(projectile.object3D);
        this.projectiles.splice(i, 1);
      }
    }
  }
  
  checkCollisions() {
    if (!this.player) return;
    
    const playerBox = this.player.getBoundingBox();
    const now =
      (globalThis.performance && typeof globalThis.performance.now === "function")
        ? globalThis.performance.now()
        : Date.now();
    
    // Player vs Enemies
    for (const enemy of this.enemies) {
      const enemyBox = enemy.getBoundingBox();
      if (playerBox.intersectsBox(enemyBox)) {
        const last = this._collisionDamageAt.get(enemy) || 0;
        if (now - last < 450) continue;
        this._collisionDamageAt.set(enemy, now);

        this.player.takeDamage(enemy.damage);
        enemy.takeDamage(1);

        // Nudge enemies away so they don't sit inside the player collider.
        if (this.player?.object3D && enemy?.object3D && enemy.position && enemy.velocity) {
          const away = new THREE.Vector3().subVectors(
            enemy.object3D.position,
            this.player.object3D.position
          );
          if (away.lengthSq() < 1e-6) {
            away.set((Math.random() - 0.5) || 1, 0, (Math.random() - 0.5) || 1);
          }
          away.normalize();
          enemy.position.add(away.clone().multiplyScalar(18));
          enemy.velocity.add(away.clone().multiplyScalar(120));
          enemy.object3D.position.copy(enemy.position);
        }
      }
    }
    
    // Player vs Power-ups
    for (let i = this.powerUps.length - 1; i >= 0; i--) {
      const powerUp = this.powerUps[i];
      const powerUpBox = powerUp.getBoundingBox();

      const pickupRadius = Number(this.player?.powerUpPickupRadius) || 0;
      const pickupBox = pickupRadius > 0 ? playerBox.clone().expandByScalar(pickupRadius) : playerBox;

      if (pickupBox.intersectsBox(powerUpBox)) {
        this.powerUpManager.collectPowerUp(powerUp, this.player);
      }
    }

    // Player vs Friendlies (civilian ramming)
    for (const friendly of this.friendlies) {
      if (!friendly || friendly.isDestroyed) continue;
      const friendlyBox = friendly.getBoundingBox();
      if (playerBox.intersectsBox(friendlyBox)) {
        const last = this._friendlyCollisionAt.get(friendly) || 0;
        if (now - last < 650) continue;
        this._friendlyCollisionAt.set(friendly, now);

        this.levelManager?.onCivilianRammed?.(friendly);

        // Nudge away for readability; do not delete the freighter instantly.
        if (friendly?.position?.isVector3 && friendly?.velocity?.isVector3) {
          const away = new THREE.Vector3().subVectors(
            friendly.object3D.position,
            this.player.object3D.position
          );
          if (away.lengthSq() < 1e-6) away.set((Math.random() - 0.5) || 1, 0, 1);
          away.normalize();
          friendly.position.add(away.clone().multiplyScalar(20));
          friendly.velocity.add(away.clone().multiplyScalar(60));
          friendly.object3D.position.copy(friendly.position);
        }
      }
    }
    
    // Projectiles vs Enemies
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      
      if (projectile.isPlayerProjectile) {
        for (const enemy of this.enemies) {
          const enemyBox = enemy.getBoundingBox();
          const projectileBox =
            typeof projectile.getSweptBoundingBox === "function"
              ? projectile.getSweptBoundingBox()
              : projectile.getBoundingBox();
          
          if (projectileBox.intersectsBox(enemyBox)) {
            // Charged shots can splash nearby enemies (Star Fox-ish "lock-on blast").
            if (projectile.isCharged && projectile.explosionRadius > 0) {
              const center = projectile.object3D.position.clone();
              const r = projectile.explosionRadius;
              const splash = Math.max(0, Math.min(1, Number(projectile.splashDamageFactor) || 0.65));
              for (const e of this.enemies) {
                if (!e?.object3D || e.isDestroyed) continue;
                const d = e.object3D.position.distanceTo(center);
                if (d > r) continue;
                const falloff = 1 - (d / r);
                const dmg = (e === enemy)
                  ? projectile.damage
                  : projectile.damage * splash * (0.35 + falloff * 0.65);
                e.takeDamage(dmg);
              }
              // Big impact cue.
              if (this.feedback?.playProjectileHit) this.feedback.playProjectileHit(center, true);
              this.feedback?.shakeScreen?.(0.12, 0.18);
            } else {
              enemy.takeDamage(projectile.damage);
            }

            if (!projectile.isCharged && this.feedback?.playProjectileHit) {
              this.feedback.playProjectileHit(projectile.object3D.position.clone(), true);
            }
            
            // Remove projectile
            this.scene.remove(projectile.object3D);
            this.projectiles.splice(i, 1);
            break;
          }
        }
      } else {
        // Enemy projectile vs friendlies OR player
        const projectileBox = projectile.getBoundingBox();

        // Friendlies take priority (protect mission).
        let hitFriendly = false;
        for (const friendly of this.friendlies) {
          if (!friendly || friendly.isDestroyed) continue;
          const friendlyBox = friendly.getBoundingBox();
          if (projectileBox.intersectsBox(friendlyBox)) {
            friendly.takeDamage(projectile.damage);
            this.levelManager?.onCivilianHit?.(friendly, projectile);
            if (this.feedback?.playProjectileHit) {
              this.feedback.playProjectileHit(projectile.object3D.position.clone(), false);
            }
            this.scene.remove(projectile.object3D);
            this.projectiles.splice(i, 1);
            hitFriendly = true;
            break;
          }
        }
        if (hitFriendly) continue;

        // Enemy projectile vs player
        if (projectileBox.intersectsBox(playerBox)) {
          this.player.takeDamage(projectile.damage);

          if (this.feedback?.playProjectileHit) {
            this.feedback.playProjectileHit(projectile.object3D.position.clone(), false);
          }

          this.scene.remove(projectile.object3D);
          this.projectiles.splice(i, 1);
        }
      }
    }
  }
  
  onResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderer.setSize(width, height);
    this.sceneManager.onResize(width, height);

    if (this.postFX?.setSize) {
      try {
        this.postFX.setSize(width, height);
      } catch {
        // ignore
      }
    }
    
    // Mobile specific optimizations
    if (width <= 768) {
      this.renderer.shadowMap.enabled = false;
    } else {
      this.renderer.shadowMap.enabled = true;
    }
    
    this.uiManager.applyResponsiveLayout();
  }
  
  // Cleanup
  dispose() {
    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
      this._animationId = null;
    }

    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    if (this._onGamepadConnected) {
      window.removeEventListener('gamepadconnected', this._onGamepadConnected);
    }
    if (this._onGamepadDisconnected) {
      window.removeEventListener('gamepaddisconnected', this._onGamepadDisconnected);
    }
    if (this._onPointerMove) {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerrawupdate', this._onPointerMove);
    }
    if (this._onMouseMove) {
      window.removeEventListener('mousemove', this._onMouseMove);
    }
    if (this._onMouseDown) {
      window.removeEventListener('mousedown', this._onMouseDown);
    }
    if (this._onMouseUp) {
      window.removeEventListener('mouseup', this._onMouseUp);
    }
    if (this._onPointerLockChange) {
      document.removeEventListener('pointerlockchange', this._onPointerLockChange);
    }
    
    if (this.postFX?.dispose) {
      try {
        this.postFX.dispose();
      } catch {
        // ignore
      }
    }
    this.postFX = null;

    if (this.renderer) {
      this.renderer.dispose();
    }
    
    if (this.sceneManager) {
      this.sceneManager.dispose();
    }
    
    if (this.player) {
      this.player.dispose();
    }

    if (this.enemyManager) this.enemyManager.dispose();
    if (this.powerUpManager) this.powerUpManager.dispose();
    if (this.friendlyManager) this.friendlyManager.clearAll();
    if (this.levelManager) this.levelManager.dispose();
    if (this.feedback) this.feedback.dispose?.();
    if (this.uiManager) this.uiManager.dispose();
    if (this.soundManager) this.soundManager.dispose?.();
  }
}
